import React, { useState } from 'react';
import {
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Divider
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { userService } from '../../../services/api';

interface SendOtpFormProps {
  onOtpSent: (email: string) => void;
  onBackToLogin: () => void;
}

const SendOtpForm: React.FC<SendOtpFormProps> = ({ onOtpSent, onBackToLogin }) => {
  const { t } = useLocalization();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await userService.forgotPassword(email);

      if (!response.success) {
        throw new Error(response.message);
      }

      onOtpSent(email);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.auth.forgotPassword.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSendOtp}>
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}
      
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter your email address and we'll send you a verification code to reset your password.
        </Typography>
      </Box>
      
      <TextField
        fullWidth
        label={t.auth.login.email}
        type="email"
        value={email}
        placeholder='Enter your email'
        onChange={(e) => setEmail(e.target.value)}
        margin="normal"
        required
        disabled={loading}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            '&:hover fieldset': {
              borderColor: 'primary.main',
            },
          },
        }}
      />
      
      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={loading}
        sx={{ 
          mt: 3, 
          mb: 2,
          py: 1.5,
          borderRadius: 2,
          textTransform: 'none',
          fontSize: '1.1rem',
          fontWeight: 'medium',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
          }
        }}
      >
        {loading ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          t.auth.forgotPassword.submit
        )}
      </Button>
      
      <Divider sx={{ my: 2 }}>
        <Typography variant="body2" color="text.secondary">
          or
        </Typography>
      </Divider>
      
      <Button
        variant="outlined"
        fullWidth
        onClick={onBackToLogin}
        disabled={loading}
        startIcon={<ArrowBackIcon />}
        sx={{ 
          py: 1.5,
          borderRadius: 2,
          fontWeight: 'medium',
          textTransform: 'none'
        }}
      >
        {t.common.back}
      </Button>
    </form>
  );
};

export default SendOtpForm; 